/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { TrashIcon } from '@heroicons/react/solid'
import { FormikProps } from 'formik'
import Image from 'next/image'
import { useRouter } from 'next/router'
import numeral from 'numeral'
import React, { useRef, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import { useDispatch } from 'react-redux'
import { AccountSection } from 'src/common/components/Cart/AccountSection'
import { BillingSection } from 'src/common/components/Cart/BillingSection'
import { ShippingSection } from 'src/common/components/Cart/ShippingSection'
import { AppLayout } from 'src/common/components/Layout/AppLayout'
import { useUser } from 'src/common/hooks/useUser'
import useJoinEventWizard from 'src/hooks/useJoinEvent'
import mainApi, { validatePromotions } from 'src/services/mainAPI'
import { wrapper } from 'src/store'
import { useAppSelector } from 'src/store/hooks'
import { removeFromBundle, removeVoucher, resetBundle } from 'src/store/joinevent/actions'
import { MBGBanner } from '../../common/components/Cart/MBGBanner'
import { OrderConfirmModal } from '../../common/components/OrderConfirmModal'

const Cart = () => {
    const [isCheckoutComplete, setIsCheckoutComplete] = useState(false)
    const user = useUser()
    const router = useRouter()
    const dispatch = useDispatch()
    const {
        cart,
        calculatedCart,
        shippableBundleProducts,
        bundleChallengeItems,
        validatePromotionsCB,
        validateVoucherCB,
        totalPrePromotion,
        challengeIds
    } = useJoinEventWizard()
    const shippingFormRef = useRef<FormikProps<any>>(null)
    const voucherFormRef = useRef<FormikProps<any>>(null)
    const bundleChallenges = useAppSelector(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        (state) => state.joinevent?.cart?.bundle?.challenges || []
    )

    // used - dont delete
    mainApi.useGetEventByIdQuery(bundleChallenges[0]?.challengeUrl ?? null, {
        skip: bundleChallenges.length === 0
    })

    const error = useAppSelector((state) => state.joinevent?.error || null)

    const { isFetching: isValidatePromotionLoading } = validatePromotions.useQueryState({
        amount: totalPrePromotion,
        challengeIds,
        userId: cart?.userId
    })

    const predefinedGiftCard = typeof cart.predefinedGiftCard !== 'undefined' &&
        cart.predefinedGiftCard !== null &&
        cart.predefinedGiftCard.balance > 0 &&
        typeof calculatedCart.vouchers.find((voucher) => voucher.isGift) ===
            'undefined' && {
            code: cart.predefinedGiftCard.code,
            balance: cart.predefinedGiftCard.balance
        }

    // const predefinedGiftCard = { code: 'test', balance: 55 }
    // for testing gift cards

    const onEditClicked = (challenge: any) => {
        if (challenge.isSwag) {
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            void router.push(`/store/${challenge.challengeUrl}`)
        } else {
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            void router.push(`/races/${challenge.challengeUrl}/join`)
        }
    }

    const onDeleteClicked = (challengeId: number) => {
        dispatch(removeFromBundle(challengeId))
        validatePromotionsCB()
    }

    const [isHovered, setIsHovered] = useState({})

    const handleMouseEnter = (idx: number) => {
        setIsHovered({ ...isHovered, [idx]: true })
    }
    const handleMouseExit = (idx: number) => {
        setIsHovered({ ...isHovered, [idx]: false })
    }
    const handlePromoDelete = (code: string, idx: number) => {
        dispatch(removeVoucher(code))
        setIsHovered({ ...isHovered, [idx]: false })
        validatePromotionsCB()
    }

    return (
        <AppLayout pageTitle="Cart">
            <OrderConfirmModal
                open={isCheckoutComplete}
                closeModal={() => {
                    setIsCheckoutComplete(false)
                    dispatch(resetBundle())
                    router.reload()
                }}
            />
            <div className="max-w-7xl mx-auto pt-3 md:pt-8">
                <MBGBanner />
                {error && (
                    <div className="block justify-items-center mb-3 p-1 bg-red-200 rounded-md text-center">
                        <p className="font-medium ">{error}</p>
                    </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-1-auto gap-x-7">
                    <div>
                        <div className="mb-2 flex justify-end pr-4 md:pr-0">
                            <a
                                href="/"
                                className="w-full md:w-auto mx-3 md:mx-0 bg-yf-purple rounded text-white text-center py-2 px-5"
                            >
                                Continue Shopping
                            </a>
                        </div>
                        <AccountSection />

                        {shippableBundleProducts.length ? (
                            <ShippingSection formRef={shippingFormRef} />
                        ) : null}

                        {user ? (
                            <BillingSection
                                shippingFormRef={shippingFormRef}
                                voucherFormRef={voucherFormRef}
                                onAddVoucher={validateVoucherCB}
                                predefinedGiftCard={predefinedGiftCard}
                                setCheckoutComplete={setIsCheckoutComplete}
                            />
                        ) : null}
                    </div>

                    <div className="shadow-md rounded-md p-8 w-full border md:min-w-[400px] md:max-w-[400px] border-gray-200 bg-white">
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <p className="text-2xl font-medium">Order Summary</p>
                                <p className="font-medium text-yf-green">
                                    {bundleChallengeItems.length > 0
                                        ? bundleChallengeItems.length
                                        : bundleChallenges.length}{' '}
                                    items
                                </p>
                            </div>

                            {bundleChallengeItems.length > 0 ? (
                                <div>
                                    <button
                                        className="text-red-500"
                                        onClick={() => dispatch(resetBundle())}
                                    >
                                        Clear cart
                                    </button>
                                </div>
                            ) : null}
                        </div>

                        <div className="h-px bg-gray-300 mb-8"></div>
                        {bundleChallenges.map((challenge, index) => (
                            <div
                                className="rounded-lg shadow-md mb-6 border border-gray-200"
                                key={index}
                            >
                                <div className="p-4">
                                    <div>
                                        <p className="text-yf-purple font-medium text-lg">
                                            {challenge.name}
                                        </p>
                                    </div>
                                    <div>
                                        {challenge.items.map((item, idx) => {
                                            return (
                                                <div className="my-2" key={idx}>
                                                    <p className="font-medium text-black">
                                                        {' '}
                                                        {item.description}
                                                    </p>
                                                    <p className="text-yf-green">
                                                        ${item.itemPrice}
                                                    </p>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                                <div className="bg-gray-100 px-10 py-3 flex justify-between">
                                    <button
                                        className="text-yf-purple"
                                        onClick={() => onEditClicked(challenge)}
                                    >
                                        Edit
                                    </button>
                                    {!cart.bundle.paymentId ? (
                                        <button
                                            className="text-red-500"
                                            onClick={() =>
                                                onDeleteClicked(challenge.challengeId)
                                            }
                                        >
                                            Delete
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        ))}
                        {bundleChallengeItems.length === 0 &&
                            bundleChallenges.length === 0 && (
                                <div className="flex flex-col">
                                    <Image
                                        alt="Empty Cart"
                                        src="/empty-cart.png"
                                        width={200}
                                        height={275}
                                        key={`empty-cart`}
                                        unoptimized
                                        objectFit="contain"
                                        className="flex flex-1 justify-center"
                                        loader={({ src }) => {
                                            return `${src}`
                                        }}
                                    />
                                    <h1 className=" flex flex-1 mt-10 justify-center text-xl font-medium text-gray-600 text-center">
                                        Your cart is empty!
                                    </h1>
                                    <h2 className="flex flex-1 mt-2 justify-center text-base font-base text-gray-600 text-center">
                                        Add Races or Store items now to begin your fitness
                                        journey.
                                    </h2>
                                </div>
                            )}
                        {bundleChallengeItems.length > 0 && (
                            <div className="mt-10">
                                <div className="flex justify-between mb-2">
                                    <p className="text-gray-600 text-xl font-medium">
                                        Subtotal
                                    </p>
                                    <p className="text-gray-600 text-xl font-medium">
                                        {numeral(calculatedCart.subTotal).format(
                                            '$0[.]00'
                                        )}
                                    </p>
                                </div>

                                <div className="flex justify-between">
                                    <p className="text-gray-600 text-xl font-medium">
                                        Shipping
                                    </p>
                                    <p className="text-yf-purple text-xl font-medium">
                                        {calculatedCart?.rows?.find(
                                            (row) =>
                                                row?.description ===
                                                'Shipping and Handling'
                                        )?.amount ?? ''}
                                    </p>
                                </div>

                                <div className="h-px bg-gray-300 my-4" />

                                {calculatedCart?.rows
                                    ?.filter((item: any) => item?.discount)
                                    .map((item: any, idx) => {
                                        if (
                                            calculatedCart?.promotion?.banner ===
                                                item.description ||
                                            (item.discount && !item.code)
                                        ) {
                                            return (
                                                <div
                                                    key={idx}
                                                    className="flex justify-between mb-2 p-2"
                                                >
                                                    <p className="text-gray-600 text-sm font-medium">
                                                        {item.description}
                                                    </p>
                                                    <p className="text-red-500 text-sm font-medium">
                                                        {item.amount}
                                                    </p>
                                                </div>
                                            )
                                        }
                                        return (
                                            <div
                                                key={idx}
                                                className="transition duration-500 ease-in-out flex justify-between mb-2 p-2 rounded-md hover:bg-red-50 hover:animate-pulse"
                                                onMouseEnter={() => handleMouseEnter(idx)}
                                                onMouseLeave={() => handleMouseExit(idx)}
                                                onClick={() =>
                                                    handlePromoDelete(item.code, idx)
                                                }
                                            >
                                                <p className="text-gray-600 text-sm font-medium">
                                                    {item.description}
                                                </p>
                                                <PromoPrice
                                                    item={item}
                                                    isHovered={isHovered[idx]}
                                                    key={`promo-${idx}`}
                                                />
                                            </div>
                                        )
                                    }) ?? null}

                                {!isNaN(cart.paymentAmount) && cart.paymentAmount > 0 && (
                                    <div className="flex justify-between mb-2 p-2">
                                        <p className="text-gray-600 text-sm font-medium">
                                            Previous Payment
                                        </p>
                                        <p className="text-red-500 text-sm font-medium">
                                            (
                                            {numeral(cart.paymentAmount).format(
                                                '$0[.]00'
                                            )}
                                            )
                                        </p>
                                    </div>
                                )}

                                <div className="flex justify-between">
                                    <p className="text-yf-green text-xl font-medium">
                                        TOTAL
                                    </p>
                                    <p className="text-yf-green text-xl font-medium">
                                        {isValidatePromotionLoading ? (
                                            <Skeleton width={65} />
                                        ) : (
                                            numeral(calculatedCart.total).format(
                                                '$0[.]00'
                                            )
                                        )}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </AppLayout>
    )
}

// keep here - used to read redux state from cookies
export const getServerSideProps = wrapper.getServerSideProps(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    () => async () => {
        return { props: {} }
    }
)

export default Cart

function PromoPrice({ item, isHovered }) {
    return (
        <div>
            {isHovered ? (
                <TrashIcon className="animate-pulse h-5 w-5 text-red-500" />
            ) : (
                <p className="text-red-500 text-sm font-medium">{item.amount}</p>
            )}
        </div>
    )
}
